// First-party analytics for Chipless — host-only RTDB counters (games, hands,
// sessions) that power the in-app stats. Page traffic / referrers / countries
// come from Cloudflare Web Analytics (cookieless beacon in public/index.html).
//
// Note: these analytics counters are anonymous and aggregate — they do NOT
// identify individuals. Cross-visit retention for an individual is handled
// separately by userProfile.js, which keys a small profile (name + game/hand
// counts) to the anonymous auth UID. See Privacy for how that is disclosed.
//
// RTDB rules contract (production):
//   daily_analytics  → .read: false, .write: auth != null
//   all_time_stats   → .read: false except total_hands_dealt (.read: true)
//
// Client writes MUST use update()+increment() — runTransaction() and get()+set()
// both require read permission and will fail silently under these rules.
import { database } from '../firebase/firebaseConfig';
import { ref, get, onValue, update, increment } from 'firebase/database';
import { shouldCountStats } from './shouldCountStats';

const TOTAL_HANDS_DEALT_CACHE_KEY = 'chipless_total_hands_dealt';
const TOTAL_HANDS_DEALT_PATH = 'all_time_stats/total_hands_dealt';

/** Local calendar date (YYYY-MM-DD) — matches how operators view "today" in the console. */
const getTodayDateKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const readNumberFromStorage = (key) => {
  try {
    const storedValue = localStorage.getItem(key);
    if (storedValue === null) {
      return null;
    }

    const value = Number(storedValue);
    return Number.isFinite(value) ? value : null;
  } catch (error) {
    return null;
  }
};

const writeNumberToStorage = (key, value) => {
  if (!Number.isFinite(value)) {
    return;
  }

  try {
    localStorage.setItem(key, String(value));
  } catch (error) {
    // Storage can be unavailable in private modes; analytics should remain best-effort.
  }
};

/**
 * Production-only gate for every RTDB stats write. Same helper as PostHog.
 * Hostname playchipless.com / www, no ?test=1, no zz-test* player names.
 * @param {Iterable<string>|null} [playerNames]
 */
const allowStatsWrite = (playerNames) => shouldCountStats({ playerNames });

/**
 * Increment a daily counter in Firebase Database
 * @param {string} counterName - Name of the counter to increment
 * @param {boolean} isHost - Whether current user is host
 * @param {Iterable<string>|null} [playerNames]
 */
const incrementDailyCounter = async (counterName, isHost = false, playerNames) => {
  // Only track from host devices
  if (!isHost) {
    return;
  }
  if (!allowStatsWrite(playerNames)) {
    console.log(`📊 Daily counter skipped: ${counterName} (test/preview)`);
    return;
  }

  try {
    const today = getTodayDateKey();
    // Use increment() instead of runTransaction — daily_analytics has .read: false
    // in production rules, and transactions require read permission.
    await update(ref(database, `daily_analytics/${today}`), {
      [counterName]: increment(1),
    });

    console.log(`📊 Daily counter incremented: ${today}/${counterName} +1`);
  } catch (error) {
    console.warn(`📊 Failed to increment daily counter ${counterName}:`, error);
    // Silently fail - don't break gameplay
  }
};

/**
 * Increment an all-time counter in Firebase Database
 * @param {string} counterName - Name of the counter to increment
 * @param {boolean} isHost - Whether current user is host
 * @param {Iterable<string>|null} [playerNames]
 */
const incrementAllTimeCounter = async (counterName, isHost = false, playerNames) => {
  // Only track from host devices
  if (!isHost) {
    return;
  }
  if (!allowStatsWrite(playerNames)) {
    console.log(`📊 All-time counter skipped: ${counterName} (test/preview)`);
    return;
  }

  try {
    await update(ref(database, 'all_time_stats'), {
      [counterName]: increment(1),
    });

    if (counterName === 'total_hands_dealt') {
      // Refresh the home-page cache from the value Firebase just wrote.
      // Never invent a local +1 — the live counter is the source of truth.
      const snapshot = await get(ref(database, TOTAL_HANDS_DEALT_PATH));
      const updatedTotal = snapshot.exists() ? Number(snapshot.val()) : 0;
      writeNumberToStorage(TOTAL_HANDS_DEALT_CACHE_KEY, updatedTotal);
    }

    console.log(`📊 All-time counter incremented: ${counterName} +1`);
  } catch (error) {
    console.warn(`📊 Failed to increment all-time counter ${counterName}:`, error);
    // Silently fail - don't break gameplay
  }
};

/**
 * Track game start - increments daily games_started counter
 * @param {boolean} isHost - Whether current user is host
 * @param {Iterable<string>|null} [playerNames]
 */
export const trackGameStart = (isHost, playerNames) => {
  incrementDailyCounter('games_started', isHost, playerNames);
};

/**
 * Track player join - increments daily players_joined counter
 * @param {boolean} isHost - Whether current user is host
 * @param {Iterable<string>|null} [playerNames]
 */
export const trackPlayerJoin = (isHost, playerNames) => {
  incrementDailyCounter('players_joined', isHost, playerNames);
};

/**
 * Track hand completion — RETIRED no-op.
 *
 * The `hands_completed` metric was unreliable: the fold-out call site in
 * roomManager.js gated on "does a host exist in the room", which is true on
 * every connected device, so it overcounted by ~player count. Hand volume is
 * already tracked accurately at hand START via trackNewHand (hands_dealt /
 * total_hands_dealt), so this metric is retired. Kept as a no-op so existing
 * call sites and the default export remain stable.
 * @param {boolean} _isHost - Unused.
 */
export const trackHandComplete = (_isHost) => {
  // Intentionally does nothing. See note above.
};

/**
 * Track rebuy - increments daily rebuys_processed counter
 * @param {boolean} isHost - Whether current user is host
 * @param {Iterable<string>|null} [playerNames]
 */
export const trackRebuy = (isHost, playerNames) => {
  incrementDailyCounter('rebuys_processed', isHost, playerNames);
};

/**
 * Track game end - increments daily games_ended counter
 * @param {boolean} isHost - Whether current user is host
 * @param {Iterable<string>|null} [playerNames]
 */
export const trackGameEnd = (isHost, playerNames) => {
  incrementDailyCounter('games_ended', isHost, playerNames);
};

/**
 * Track new hand - increments all-time total_hands_dealt and daily hands_dealt
 * @param {boolean} isHost - Whether current user is host
 * @param {Iterable<string>|null} [playerNames] - Room player names (zz-test* skips both counters)
 */
export const trackNewHand = (isHost, playerNames) => {
  console.log('📊 trackNewHand called:', { isHost, timestamp: Date.now(), stack: new Error().stack.split('\n')[1] });
  if (!allowStatsWrite(playerNames)) {
    console.log('📊 trackNewHand skipped daily hands_dealt and all_time_stats/total_hands_dealt (test/preview)');
    return;
  }
  incrementAllTimeCounter('total_hands_dealt', isHost, playerNames);
  incrementDailyCounter('hands_dealt', isHost, playerNames);
};

/**
 * Track page visit - increments daily page_visits counter AND all-time total_page_visits
 */
export const trackPageVisit = async () => {
  if (!allowStatsWrite()) {
    console.log('📊 Page visit skipped (test/preview)');
    return;
  }

  try {
    const today = getTodayDateKey();

    await Promise.all([
      update(ref(database, `daily_analytics/${today}`), {
        page_visits: increment(1),
      }),
      update(ref(database, 'all_time_stats'), {
        total_page_visits: increment(1),
      }),
    ]);

    console.log(`📊 Page visit tracked: ${today} +1`);
  } catch (error) {
    console.warn('📊 Failed to track page visit:', error);
    // Silently fail - don't break the app
  }
};

/**
 * Track game session start - records when actual poker gameplay begins
 * Only tracks time when gameState.status = "PLAYING"
 * @returns {string} sessionId for tracking this session
 */
export const trackSessionStart = () => {
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const startTime = Date.now();
  
  // Store session start time in localStorage
  localStorage.setItem('chipless_session_id', sessionId);
  localStorage.setItem('chipless_session_start', startTime.toString());
  
  console.log(`📊 Session started: ${sessionId}`);
  return sessionId;
};

/**
 * Track game session end - calculates actual poker gameplay duration
 * Only counts time spent in PLAYING state, not lobby/settings time
 * @param {Iterable<string>|null} [playerNames]
 */
export const trackSessionEnd = async (playerNames) => {
  try {
    const sessionId = localStorage.getItem('chipless_session_id');
    const startTime = localStorage.getItem('chipless_session_start');
    
    if (!sessionId || !startTime) {
      console.warn('📊 No session data found');
      return;
    }

    const clearSession = () => {
      localStorage.removeItem('chipless_session_id');
      localStorage.removeItem('chipless_session_start');
    };

    if (!allowStatsWrite(playerNames)) {
      console.log('📊 Session end skipped (test/preview)');
      clearSession();
      return;
    }
    
    const endTime = Date.now();
    const sessionDurationMs = endTime - parseInt(startTime);
    const sessionDurationMinutes = Math.round(sessionDurationMs / (1000 * 60));
    
    const today = getTodayDateKey();

    // Write-only increments — neither daily_analytics nor all_time_stats/sessions
    // is readable from the client. Average session length is derived in the console
    // from count + totalMinutes.
    await Promise.all([
      update(ref(database, `daily_analytics/${today}/sessions`), {
        count: increment(1),
        totalMinutes: increment(sessionDurationMinutes),
      }),
      update(ref(database, 'all_time_stats/sessions'), {
        count: increment(1),
        totalMinutes: increment(sessionDurationMinutes),
      }),
    ]);
    
    console.log(`📊 Session ended: ${sessionDurationMinutes} minutes`);

    clearSession();
    
  } catch (error) {
    console.warn('📊 Failed to track session end:', error);
  }
};

/**
 * Get the cached total hands dealt all-time for instant home-page rendering.
 * @returns {number|null} Cached total hands dealt, or null if unavailable
 */
export const getCachedTotalHandsDealt = () => {
  return readNumberFromStorage(TOTAL_HANDS_DEALT_CACHE_KEY);
};

/**
 * Get the total hands dealt all-time
 * @returns {Promise<number|null>} Total hands dealt, or null if unavailable
 */
export const getTotalHandsDealt = async () => {
  try {
    const totalHandsRef = ref(database, TOTAL_HANDS_DEALT_PATH);
    const snapshot = await get(totalHandsRef);
    const total = snapshot.exists() ? Number(snapshot.val()) : 0;
    writeNumberToStorage(TOTAL_HANDS_DEALT_CACHE_KEY, total);
    return Number.isFinite(total) ? total : null;
  } catch (error) {
    console.warn('📊 Failed to get total hands dealt:', error);
    return null;
  }
};

/**
 * Live listener on the same all-time hands path the home page reads.
 * @param {(total: number) => void} onChange
 * @returns {() => void} Unsubscribe
 */
export const subscribeTotalHandsDealt = (onChange) => {
  const totalHandsRef = ref(database, TOTAL_HANDS_DEALT_PATH);
  return onValue(
    totalHandsRef,
    (snapshot) => {
      const total = snapshot.exists() ? Number(snapshot.val()) : 0;
      if (!Number.isFinite(total)) return;
      writeNumberToStorage(TOTAL_HANDS_DEALT_CACHE_KEY, total);
      onChange(total);
    },
    (error) => {
      console.warn('📊 Failed to subscribe to total hands dealt:', error);
    }
  );
};

/**
 * Get daily page visits for today.
 * Requires `.read: true` on daily_analytics — blocked in production rules.
 * Reserved for admin tooling; not used in the app UI.
 * @returns {Promise<number>} Today's page visits
 */
export const getTodayPageVisits = async () => {
  try {
    const today = getTodayDateKey();
    const dailyVisitsRef = ref(database, `daily_analytics/${today}/page_visits`);
    const snapshot = await get(dailyVisitsRef);
    return snapshot.exists() ? snapshot.val() : 0;
  } catch (error) {
    console.warn('📊 Failed to get today page visits:', error);
    return 0;
  }
};

/**
 * Get average session time for today.
 * Requires `.read: true` on daily_analytics — blocked in production rules.
 * Reserved for admin tooling; not used in the app UI.
 * @returns {Promise<number>} Today's average session time in minutes
 */
export const getTodayAverageSessionTime = async () => {
  try {
    const today = getTodayDateKey();
    const dailySessionsRef = ref(database, `daily_analytics/${today}/sessions`);
    const snapshot = await get(dailySessionsRef);
    if (!snapshot.exists()) return 0;
    const data = snapshot.val();
    if (data.averageMinutes != null) return data.averageMinutes;
    if (data.count > 0 && data.totalMinutes != null) {
      return Math.round(data.totalMinutes / data.count);
    }
    return 0;
  } catch (error) {
    console.warn('📊 Failed to get today average session time:', error);
    return 0;
  }
};

export default {
  trackGameStart,
  trackPlayerJoin,
  trackHandComplete,
  trackRebuy,
  trackGameEnd,
  trackNewHand,
  getCachedTotalHandsDealt,
  getTotalHandsDealt,
  subscribeTotalHandsDealt,
  trackPageVisit,
  trackSessionStart,
  trackSessionEnd,
  getTodayPageVisits,
  getTodayAverageSessionTime
};
